<template>
  <div class="d-flex flex-column ga-3" role="status" data-cc="bekki-thinking">
    <p class="bekki-thinking-steps__title align-self-start text-body-1">
      {{ t('widgets.bekki.thinking.title') }}
    </p>

    <ol class="bekki-thinking-steps d-flex flex-column">
      <li
        v-for="step in steps"
        :key="step.id"
        :class="[
          'bekki-thinking-steps__step position-relative d-flex align-start ga-2',
          `bekki-thinking-steps__step--${step.state}`
        ]"
        :data-state="step.state"
        data-cc="bekki-thinking-step"
      >
        <span class="bekki-thinking-steps__marker flex-shrink-0">
          <span
            class="bekki-thinking-steps__ring d-flex align-center justify-center rounded-circle border-md"
          >
            <span class="bekki-thinking-steps__dot rounded-circle" />
          </span>

          <v-icon
            icon="huge:checkmark-circle-solid"
            color="primary"
            size="20"
            class="bekki-thinking-steps__check"
          />
        </span>

        <span
          :class="[
            'bekki-thinking-steps__label text-body-1',
            {
              'text-medium-emphasis': step.state === 'pending',
              'text-disabled': step.state === 'done'
            }
          ]"
        >
          {{ labels[step.id] }}
        </span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';

import type {
  DisplayedThinkingStep,
  ThinkingStepId
} from '@/composables/api/bekki/types';

interface Props {
  steps: DisplayedThinkingStep[];
}

defineProps<Props>();

const { t } = useI18n();

const labels = computed((): Record<ThinkingStepId, string> => ({
  reading: t('widgets.bekki.thinking.reading'),
  analyzing: t('widgets.bekki.thinking.analyzing'),
  synthesizing: t('widgets.bekki.thinking.synthesizing')
}));
</script>

<style scoped>
.bekki-thinking-steps {
  list-style: none;
  row-gap: 31px;
}

.bekki-thinking-steps__title,
.bekki-thinking-steps__step--active .bekki-thinking-steps__label {
  color: transparent;
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), var(--bm-opacity-40)) 0% 30%,
    rgba(var(--v-theme-on-surface), var(--bm-opacity-95)) 50%,
    rgba(var(--v-theme-on-surface), var(--bm-opacity-40)) 70% 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  animation: bekki-thinking-shimmer 2s linear infinite;
}

.bekki-thinking-steps__step + .bekki-thinking-steps__step::before {
  content: '';
  position: absolute;
  bottom: calc(100% + var(--bm-spacing-2));
  left: var(--bm-spacing-2);
  width: var(--bm-spacing-half);
  height: 22px;
  border-radius: var(--bm-rounded-sm);
  background-color: rgba(var(--v-theme-primary), var(--v-opacity-darker));
}

.bekki-thinking-steps__marker {
  display: grid;
  grid-template-columns: 18px;
  place-items: center;
}

.bekki-thinking-steps__ring,
.bekki-thinking-steps__check {
  grid-area: 1 / 1;
}

.bekki-thinking-steps__ring {
  --v-border-color: var(--v-theme-on-surface);
  --v-border-opacity: var(--v-opacity-darker);
  width: 18px;
  height: 18px;
  transition:
    opacity 320ms ease,
    border-color 320ms ease;
}

.bekki-thinking-steps__dot {
  width: 6px;
  height: 6px;
  background-color: rgba(var(--v-theme-on-surface), var(--v-opacity-darker));
  transition: background-color 320ms ease;
}

.bekki-thinking-steps__check {
  opacity: 0;
  transform: scale(0.5);
  transition:
    opacity 320ms ease,
    transform 320ms ease;
}

.bekki-thinking-steps__label {
  transition: color 320ms ease;
}

.bekki-thinking-steps__step--active .bekki-thinking-steps__ring {
  --v-border-color: var(--v-theme-primary);
  --v-border-opacity: 1;
}

.bekki-thinking-steps__step--active .bekki-thinking-steps__dot {
  background-color: rgb(var(--v-theme-primary));
  animation: bekki-thinking-pulse 1.4s ease-in-out infinite;
}

.bekki-thinking-steps__step--active .bekki-thinking-steps__label::after {
  content: '...';
}

.bekki-thinking-steps__step--done .bekki-thinking-steps__ring {
  opacity: 0;
}

.bekki-thinking-steps__step--done .bekki-thinking-steps__check {
  opacity: 1;
  transform: scale(1);
}

@keyframes bekki-thinking-shimmer {
  from {
    background-position: 100% 0;
  }

  to {
    background-position: -100% 0;
  }
}

@keyframes bekki-thinking-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(0.7);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bekki-thinking-steps__title,
  .bekki-thinking-steps__step--active .bekki-thinking-steps__label {
    background: none;
    animation: none;
  }

  .bekki-thinking-steps__title {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  .bekki-thinking-steps__step--active .bekki-thinking-steps__label {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  .bekki-thinking-steps__dot {
    animation: none;
  }

  .bekki-thinking-steps__ring,
  .bekki-thinking-steps__dot,
  .bekki-thinking-steps__check,
  .bekki-thinking-steps__label {
    transition: none;
  }
}
</style>
